import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import { submitRecognition, getCompanyRecognitionValues } from '../../../../api/PreformanceManagement/Recognition';
import { useWriteAccess, RESTRICTED_WRITE_REASON_ID } from '../../../../hooks/useWriteAccess';
import { RestrictedWriteHint } from '../../../../components/subscription/RestrictedWriteNotice';
import { CompanyRecognitionValueView } from '../../../../models/preformanceManagement';

export interface InlineRecognitionAiDraft {
  name: string;
  aiAvailable: boolean;
  noGoals: boolean;
}

interface InlineRecognitionFormProps {
  recipientId: string;
  recipientName: string;
  /** Pre-filled message, e.g. an AI draft. The person always edits before sending. */
  initialMessage?: string;
  /** Present only when the message was AI-drafted — drives the advisory banner. */
  aiDraft?: InlineRecognitionAiDraft | null;
  onCancel: () => void;
  /** Fired after a successful send so the caller can refresh its list. */
  onSent: () => void;
}

/**
 * A deliberately small recognition form that opens directly beneath the person it is about.
 *
 * The full composer lives on the feed tabs and asks for everything — recipient, visibility,
 * a skill, values, message. Under a gap row most of that is already known or beside the
 * point: you are recognizing THIS person, right now. Sending someone to the top of the page
 * to fill in a large form, then back down to find their row again, is the friction this
 * removes. Recipient is fixed by the row; only the value and the words are asked for.
 */
export default function InlineRecognitionForm({
  recipientId,
  recipientName,
  initialMessage = '',
  aiDraft = null,
  onCancel,
  onSent,
}: InlineRecognitionFormProps) {
  const { t } = useTranslation();
  // CO-24: this inline form is a second recognition-write surface; it must honour the
  // same billing restricted-write gate as the full composer on the feed.
  const write = useWriteAccess();
  const [message, setMessage] = useState(initialMessage);
  const [values, setValues] = useState<CompanyRecognitionValueView[]>([]);
  const [valueName, setValueName] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  // The form is opened by a deliberate click, so put the caret where the person will type.
  // For a draft this also signals "your text arrived" to anyone not watching the screen.
  useEffect(() => {
    const el = messageRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCompanyRecognitionValues()
      .then((result) => {
        if (cancelled || result.errors.length > 0) return;
        const list = result.payload ?? [];
        setValues(list);
        if (list.length === 1) setValueName(list[0].name);
      })
      .catch(() => { /* the picker simply stays empty; send still validates below */ });
    return () => { cancelled = true; };
  }, []);

  const handleSend = async () => {
    if (!message.trim()) {
      setError(t('performance.recognition.gaps.inline.messageRequired'));
      return;
    }
    if (!valueName) {
      setError(t('performance.recognition.gaps.inline.valueRequired'));
      return;
    }
    setError(null);
    setSending(true);
    try {
      const result = await submitRecognition({
        recipientId,
        message: message.trim(),
        values: [valueName],
      });
      if (result.errors.length > 0) {
        setError(t('performance.recognition.gaps.inline.sendFailed'));
        return;
      }
      onSent();
    } catch {
      setError(t('performance.recognition.gaps.inline.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mt-3 space-y-3 rounded-lg border border-border bg-muted/30 p-3"
      data-testid={`inline-recognition-form-${recipientId}`}
    >
      <p className="text-xs font-medium text-foreground">
        {t('performance.recognition.gaps.inline.heading', { name: recipientName })}
      </p>

      {aiDraft && (
        <div
          role="status"
          data-testid="inline-recognition-ai-banner"
          className="rounded-md border border-amber-400 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
        >
          <div className="flex items-start gap-1.5">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t('performance.recognition.gaps.aiDraftBanner', { name: aiDraft.name })}
              {!aiDraft.aiAvailable && ` ${t('performance.recognition.gaps.aiDraftFallbackNote')}`}
            </span>
          </div>
          {aiDraft.noGoals && (
            <p className="mt-1.5 ps-5">{t('performance.recognition.gaps.aiDraftNoGoals')}</p>
          )}
        </div>
      )}

      <Select
        options={values.map((v) => ({ label: v.name, value: v.name }))}
        value={valueName}
        onChange={(val: string) => { setValueName(val); setError(null); }}
        placeholder={t('performance.recognition.gaps.inline.valuePlaceholder')}
        data-testid={`inline-recognition-value-${recipientId}`}
      />

      <textarea
        ref={messageRef}
        rows={3}
        value={message}
        onChange={(e) => { setMessage(e.target.value); setError(null); }}
        placeholder={t('performance.recognition.gaps.inline.messagePlaceholder', { name: recipientName })}
        maxLength={500}
        aria-label={t('performance.recognition.gaps.inline.messageLabel')}
        data-testid={`inline-recognition-message-${recipientId}`}
        className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
      />

      {error && (
        <p role="alert" className="text-xs text-destructive" data-testid={`inline-recognition-error-${recipientId}`}>
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={sending}>
          {t('common.cancel')}
        </Button>
        <Button
          size="sm"
          onClick={handleSend}
          loading={sending}
          {...(write.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})}
          disabled={sending || write.blocked}
          data-testid={`inline-recognition-send-${recipientId}`}
        >
          {t('performance.recognition.gaps.inline.send')}
        </Button>
      </div>
      {write.blocked && <RestrictedWriteHint />}
    </div>
  );
}
